import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Linkedin, Github, FileText, MapPin, Calendar, ExternalLink } from 'lucide-react';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { Separator } from './components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from './components/ui/avatar';
import { Card, CardContent, CardHeader } from './components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './components/ui/tooltip';
import { Timeline, TimelineItem, TimelineIcon, TimelineContent } from './components/ui/timeline';
import blueVoyantLogo from './assets/img/bluevoyant_logo.png';
import klaviyoLogo from './assets/img/klaviyo-logo.svg';
import ibmLogo from './assets/img/IBM_logo.svg';
import fidelityLogo from './assets/img/fidelity-investments-logo.png';
import symboticLogo from './assets/img/SYM.svg';
import umassLogo from './assets/img/UMass_Lowell_logo.svg.png';
import { StarsBackground } from './components/animate-ui/backgrounds/stars';

function App() {
  const skills = {
    'Languages': [
      { name: 'TypeScript', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg' },
      { name: 'JavaScript', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg' },
      { name: 'Python', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg' },
      { name: 'Golang', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/go/go-original.svg' }
    ],
    'Frontend': [
      { name: 'React', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg' },
      { name: 'Tailwind', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tailwindcss/tailwindcss-original.svg' },
      { name: 'Material-UI', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/materialui/materialui-original.svg' },
      { name: 'D3.js', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/d3js/d3js-original.svg' }
    ],
    'Backend': [
      { name: 'Express.js', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/express/express-original.svg' },
      { name: 'Django', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/django/django-plain.svg' },
      { name: 'GraphQL', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/graphql/graphql-plain.svg' },
      { name: 'Fastify', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/fastify/fastify-original.svg' }
    ],
    'Data & Cloud': [
      { name: 'PostgreSQL', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg' },
      { name: 'Supabase', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/supabase/supabase-original.svg' },
      { name: 'Azure', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/azure/azure-original.svg' },
      { name: 'AWS', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original-wordmark.svg' }
    ]
  };

  const experience = [
    {
      company: 'BlueVoyant',
      logo: blueVoyantLogo,
      url: 'https://www.bluevoyant.com',
      role: 'Full-Stack Software Engineer, Managed Security Services',
      period: 'May 2023 - February 2026',
      location: 'New York, NY',
      highlights: [
        "Adopted Conquest Cyber's tech stack following BlueVoyant's acquisition, establishing documentation and onboarding practices across the engineering organization; sole owner of the MITRE ATT&CK Coverage Dashboard from development through production, surfacing Azure security coverage for 550+ businesses",
        'Served as technical lead for a 4-engineer team, driving end-to-end planning and delivery of business-facing security dashboards including Threat Intelligence features for the full client base and Threat Hunt insights for ~150 businesses',
        'Shipped GraphQL queries and resolvers across Go microservices in an Apollo Federation architecture, supplying data to React/Tailwind dashboards with Godog integration tests and Playwright end-to-end coverage',
        'Developed TypeScript ETL pipelines that ingested and normalized security telemetry from Azure, MITRE ATT&CK, and OpenCTI sources into Elasticsearch, providing the data queried by Go microservices across all platform dashboards',
        'Delivered full-stack features for an internal Azure Sentinel Automation Portal as part of a 5-engineer team, implementing React and Material UI components alongside an Express.js and PostgreSQL backend to enable deployment engineers to provision, update, and audit customer Azure resources'
      ]
    },
    {
      company: 'Klaviyo',
      logo: klaviyoLogo,
      url: 'https://www.klaviyo.com',
      role: 'Software Engineer, Klaviyo Operations',
      period: 'May 2021 - August 2022',
      location: 'Boston, MA',
      highlights: [
        'Decoupled monolithic ETL processes into microservices that ingested queue-based messages to index Help Center articles into Algolia and process Community Forum badge data, improving scalability and availability via Kubernetes on Amazon EKS',
        'Worked with UI designers through Figma to redesign and develop a modernized Klaviyo Help Center search experience using Javascript, HTML, and CSS',
        'Used React.js with Typescript along with Klaviyo\'s internal component library to create Klaviyo\'s Support Portal to educate and help Klaviyo users with questions related to using the platform',
        'Created API endpoints in Django to carry out backend operations that utilize Django\'s object-relational mapper across different Klaviyo services including Klaviyo\'s Support Portal',
        'Wrote test cases ranging from unit, integration, and end-to-end tests using testing frameworks such as Pytest and the React Testing Library to ensure outstanding code quality from both the backend and frontend'
      ]
    },
    {
      company: 'IBM',
      logo: ibmLogo,
      url: 'https://www.ibm.com',
      role: 'Full-Stack Developer, IBM Finance and Operations & IBM Financial Crimes Insight',
      period: 'January 2020 - April 2021',
      location: 'Research Triangle Park, NC',
      highlights: [
        'Redesigned and implemented new graphical interfaces into IBM\'s Financial Crimes Insight web application using React.js and D3.js',
        'Managed current and newly-added Node.js packages used in IBM\'s Financial Crimes Insight web application to ensure that they were compliant for legal use in the application',
        'Updated legacy React.js and IBM\'s Carbon Design System code in the application to the latest version',
        'Used Python and CSS from IBM\'s Carbon Design System to automate and generate HTML reports for an easier view of fraudulent companies within clusters of data'
      ]
    },
    {
      company: 'Fidelity Investments',
      logo: fidelityLogo,
      url: 'https://www.fidelity.com',
      role: 'Software Engineering Intern',
      period: 'May - August 2019',
      location: 'Merrimack, NH',
      highlights: [
        'Built Cloud Intuition SPA with Angular to display AWS config events and ServiceNow tickets in timeline',
        'Deployed application and AWS Lambdas using Jenkins automation',
        'Created CloudFormation templates for Jenkins deployment of Cloud Intuition and Lambdas'
      ]
    },
    {
      company: 'Fidelity Investments',
      logo: '',
      url: 'https://www.fidelity.com',
      role: 'Software Engineering Intern',
      period: 'May - August 2018',
      location: 'Merrimack, NH',
      highlights: [
        'Developed dashboard integrating test data from IBM UrbanCode Deploy and HP Quality Center using React.js',
        'Collaborated on API integration to push IBM UrbanCode Deploy data to QA team database',
        'Documented data flow and integration processes'
      ]
    },
    {
      company: 'Symbotic',
      logo: symboticLogo,
      url: 'https://www.symbotic.com',
      role: 'Software Engineering Co-op',
      period: 'January - July 2017',
      location: 'Wilmington, MA',
      highlights: [
        'Implemented features and bug fixes for warehouse automation software',
        'Built Python automation scripts reducing software stack startup time',
        'Automated end-to-end and unit tests in GitLab CI/CD pipelines'
      ]
    }
  ];

  const projects = [
    { name: 'BlueVoyant Cyber Defense Platform', tech: 'Go, React, TypeScript', url: 'https://www.youtube.com/watch?v=gwXj6OpmMxk' },
    { name: 'Klaviyo Support Portal', tech: 'React, Django', url: 'https://www.klaviyo.com/login?next=/support' },
    { name: 'Klaviyo Help Center', tech: 'HTML, CSS, JS', url: 'https://help.klaviyo.com/hc/en-us' },
  ];

  return (
    <TooltipProvider>
    <StarsBackground className="min-h-screen" starColor="#3B82F6">
    <div className="min-h-screen">
      {/* Compact Hero */}
      <header className="sticky top-0 z-10 border-b border-white/10 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Avatar className="h-12 w-12 border-2 border-primary/20">
                <AvatarImage src="/logo192.png" alt="Kody Thach" />
                <AvatarFallback className="text-base">KT</AvatarFallback>
              </Avatar>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Kody Thach</h1>
                <p className="text-sm text-muted-foreground">Full-Stack Engineer</p>
              </div>
            </div>
            <div className="flex flex-col-reverse sm:flex-row items-end sm:items-center gap-2 sm:gap-3">
              <Button size="sm" asChild>
                <a href="mailto:kodythach@gmail.com">
                  <Mail className="w-4 h-4 mr-2" />
                  Contact
                </a>
              </Button>
              <Button size="sm" variant="outline" asChild>
                <a href="/Kody_Thach_Resume.pdf" target="_blank">
                  <FileText className="w-4 h-4 mr-2" />
                  Resume
                </a>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-12">
        {/* Skills Grid */}
        <section>
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <div className="h-6 w-1 bg-primary rounded-full" />
            Technical Skills
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(skills).map(([category, items]) => (
              <motion.div
                key={category}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <Card className="h-full">
                  <CardHeader className="pb-3">
                    <h3 className="text-sm font-semibold text-primary">{category}</h3>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {items.map((skill) => (
                        <Tooltip key={skill.name} delayDuration={200}>
                          <TooltipTrigger asChild>
                            <div className="w-full h-10 flex items-center justify-center rounded-lg bg-white/90 p-2 cursor-default">
                              <img src={skill.icon} alt={skill.name} className="w-full h-full object-contain pointer-events-none" />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{skill.name}</p>
                          </TooltipContent>
                        </Tooltip>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Experience Timeline */}
        <section>
          <h2 className="text-xl font-semibold mb-8 flex items-center gap-2">
            <div className="h-6 w-1 bg-primary rounded-full" />
            Experience
          </h2>
          <Timeline>
            {experience.map((job, index) => (
              <TimelineItem key={index}>
                {job.logo && (
                  <TimelineIcon>
                    <a
                      href={job.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block hover:scale-110 transition-transform"
                    >
                      <img src={job.logo} alt={job.company} className="w-full h-full object-contain" />
                    </a>
                  </TimelineIcon>
                )}
                <TimelineContent>
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className="group cursor-default">
                      <CardHeader className="pb-4">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                          <div>
                            <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                              {job.company}
                            </h3>
                            <p className="text-sm text-muted-foreground">{job.role}</p>
                          </div>
                          <div className="flex flex-col sm:items-end gap-1 text-xs text-muted-foreground shrink-0">
                            <div className="flex items-center gap-1.5">
                              <Calendar className="w-3 h-3" />
                              {job.period}
                            </div>
                            <div className="flex items-center gap-1.5">
                              <MapPin className="w-3 h-3" />
                              {job.location}
                            </div>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-1.5">
                          {job.highlights.map((highlight, i) => (
                            <li key={i} className="text-sm text-muted-foreground flex gap-2">
                              <span className="text-primary select-none">•</span>
                              <span className="flex-1">{highlight}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </motion.div>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </section>

        {/* Education */}
        <section>
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <div className="h-6 w-1 bg-primary rounded-full" />
            Education & Certifications
          </h2>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white rounded-lg p-2 flex items-center justify-center">
                      <img src={umassLogo} alt="UMass Lowell" className="w-8 h-8 object-contain" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">University of Massachusetts Lowell</h3>
                      <p className="text-sm text-muted-foreground">Bachelor of Science in Computer Science</p>
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground shrink-0">
                    <span className="text-primary font-semibold">December 2019</span>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                <Separator />

                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-3">Certifications</h4>
                  <div className="grid sm:grid-cols-2 gap-3 text-sm">
                    <motion.div
                      className="flex justify-between items-center gap-2"
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.1 }}
                    >
                      <a href="https://learn.microsoft.com/en-us/users/kodythach/credentials/5be4b9522d17314c" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2">
                        <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/azure/azure-original.svg" alt="Microsoft" className="w-4 h-4" />
                        Microsoft Certified: Azure AI Engineer Associate
                        <ExternalLink className="w-3 h-3 opacity-50" />
                      </a>
                      <Badge variant="outline" className="bg-primary/5 shrink-0">Jul 2025</Badge>
                    </motion.div>
                    <motion.div
                      className="flex justify-between items-center gap-2"
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2 }}
                    >
                      <a href="https://learn.microsoft.com/en-us/users/kodythach/credentials/313710e52345270f" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2">
                        <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/azure/azure-original.svg" alt="Microsoft" className="w-4 h-4" />
                        Microsoft Certified: Azure AI Fundamentals
                        <ExternalLink className="w-3 h-3 opacity-50" />
                      </a>
                      <Badge variant="outline" className="bg-primary/5 shrink-0">Jul 2025</Badge>
                    </motion.div>
                    <motion.div
                      className="flex justify-between items-center gap-2"
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 }}
                    >
                      <a href="https://learn.microsoft.com/en-us/users/kodythach/credentials/a04e0602811fada7" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2">
                        <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/azure/azure-original.svg" alt="Microsoft" className="w-4 h-4" />
                        Microsoft Certified: Azure Solutions Architect Expert
                        <ExternalLink className="w-3 h-3 opacity-50" />
                      </a>
                      <Badge variant="outline" className="bg-primary/5 shrink-0">Sep 2024</Badge>
                    </motion.div>
                    <motion.div
                      className="flex justify-between items-center gap-2"
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.4 }}
                    >
                      <a href="https://learn.microsoft.com/en-us/users/kodythach/credentials/34499b68ffbca5e3" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2">
                        <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/azure/azure-original.svg" alt="Microsoft" className="w-4 h-4" />
                        Microsoft Certified: Azure Administrator
                        <ExternalLink className="w-3 h-3 opacity-50" />
                      </a>
                      <Badge variant="outline" className="bg-primary/5 shrink-0">Aug 2024</Badge>
                    </motion.div>
                    <motion.div
                      className="flex justify-between items-center gap-2"
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.5 }}
                    >
                      <a href="https://www.apollographql.com/tutorials/certifications/dbdc3515-59b7-4285-8717-e86a5e95cf37" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2">
                        <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/graphql/graphql-plain.svg" alt="Apollo GraphQL" className="w-4 h-4" />
                        Graph Developer - Professional
                        <ExternalLink className="w-3 h-3 opacity-50" />
                      </a>
                      <Badge variant="outline" className="bg-primary/5 shrink-0">Feb 2024</Badge>
                    </motion.div>
                    <motion.div
                      className="flex justify-between items-center gap-2"
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.6 }}
                    >
                      <a href="https://www.apollographql.com/tutorials/certifications/54ef0e9c-34e2-4f50-b223-4650ddb8ac3c" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2">
                        <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/graphql/graphql-plain.svg" alt="Apollo GraphQL" className="w-4 h-4" />
                        Graph Developer - Associate
                        <ExternalLink className="w-3 h-3 opacity-50" />
                      </a>
                      <Badge variant="outline" className="bg-primary/5 shrink-0">Jan 2024</Badge>
                    </motion.div>
                    <motion.div
                      className="flex justify-between items-center gap-2"
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.7 }}
                    >
                      <a href="https://learn.microsoft.com/en-us/users/kodythach/credentials/25eaf199aac90a07" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2">
                        <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/azure/azure-original.svg" alt="Microsoft" className="w-4 h-4" />
                        Microsoft Certified: Azure Fundamentals
                        <ExternalLink className="w-3 h-3 opacity-50" />
                      </a>
                      <Badge variant="outline" className="bg-primary/5 shrink-0">Dec 2023</Badge>
                    </motion.div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </section>

        {/* Projects */}
        <section>
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <div className="h-6 w-1 bg-primary rounded-full" />
            Featured Projects
          </h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {projects.map((project, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <a
                  href={project.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block h-full"
                >
                  <Card className="h-full group cursor-pointer hover:border-primary/50 transition-all">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                          {project.name}
                        </h3>
                        <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                      </div>
                      <p className="text-xs text-muted-foreground pt-1">{project.tech}</p>
                    </CardHeader>
                  </Card>
                </a>
              </motion.div>
            ))}
          </div>
        </section>
      </main>

      {/* Compact Footer */}
      <footer className="border-t border-border/40 mt-16 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <p>© 2025 Kody Thach</p>
            <div className="flex items-center gap-4">
              <a href="https://github.com/kodycode" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors flex items-center gap-1.5">
                <Github className="w-5 h-5" />
              </a>
              <a href="https://linkedin.com/in/kodythach" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors flex items-center gap-1.5">
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
    </StarsBackground>
    </TooltipProvider>
  );
}

export default App;
